import React, { useEffect, useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import StudentSidebar from "./StudentSidebar";
import StudentNotifications from "./StudentNotifications";
import { db, auth, storage } from "@/lib/firebase";
import { collection, getDocs, query, where, addDoc, Timestamp, doc, getDoc, updateDoc, onSnapshot } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import PaymentUpload from "@/components/Student/PaymentUpload";
import MessageDialog from "@/components/ui/MessageDialog";
import { Dialog as UIDialog, DialogContent as UIDialogContent, DialogHeader as UIDialogHeader, DialogTitle as UIDialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";

// Utility function to convert 24-hour time to 12-hour format
const formatTime12Hour = (time24: string): string => {
  if (!time24) return "";
  
  const [hours, minutes] = time24.split(':');
  const hour24 = parseInt(hours, 10);
  const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
  const ampm = hour24 >= 12 ? 'PM' : 'AM';
  
  return `${hour12}:${minutes} ${ampm}`;
};

const StudentDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState("courses");
  const [courses, setCourses] = useState<any[]>([]);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [schedule, setSchedule] = useState<any[]>([]);
  const [instructors, setInstructors] = useState<any[]>([]);
  const [message, setMessage] = useState<{ open: boolean; text: string; type?: "success" | "error" | "info" }>({ open: false, text: "" });
  const [branches, setBranches] = useState<any[]>([]);
  const [enrollDialog, setEnrollDialog] = useState<{ open: boolean; course: any | null }>({ open: false, course: null });
  const [paymentDialog, setPaymentDialog] = useState<{ open: boolean; enrollment: any | null }>({ open: false, enrollment: null });
  const [scheduleRequestDialog, setScheduleRequestDialog] = useState<{ open: boolean; enrollment: any | null }>({ open: false, enrollment: null });
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [scheduledSlots, setScheduledSlots] = useState<any[]>([]);
  const [scheduleRequests, setScheduleRequests] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userBranch, setUserBranch] = useState<string>("");
  const [userData, setUserData] = useState<any>(null);
  const [preferences, setPreferences] = useState<any>(null);
  const [editingPreferences, setEditingPreferences] = useState(false);

  // Function to fetch instructor details
  const fetchInstructors = async (instructorIds: string[]) => {
    try {
      const instructorData = [];
      for (const id of instructorIds) {
        const userDoc = await getDoc(doc(db, "users", id));
        if (userDoc.exists()) {
          const data = userDoc.data();
          instructorData.push({
            id: userDoc.id,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email
          });
        }
      }
      setInstructors(instructorData);
    } catch (error) {
      console.error("Error fetching instructors:", error);
    }
  };

  useEffect(() => {
    if (!auth.currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribers: (() => void)[] = [];

    const setupRealtimeListeners = async () => {
      try {
        // Fetch user profile by document ID (UID) - this is static data, no need for listener
        const userDoc = await getDoc(doc(db, "users", auth.currentUser!.uid));
        const userDataFetched = userDoc.data();
        setUserData(userDataFetched);
        setUserBranch(userDataFetched?.branch || "");

        // Set up real-time listener for courses
        const coursesUnsubscribe = onSnapshot(collection(db, "courses"), (snapshot) => {
          setCourses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        unsubscribers.push(coursesUnsubscribe);

        // Set up real-time listener for branches
        const branchesUnsubscribe = onSnapshot(collection(db, "branches"), (snapshot) => {
          const fetchedBranches = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setBranches(fetchedBranches);
        });
        unsubscribers.push(branchesUnsubscribe);

        // Set up real-time listener for enrollments for this student
        const enrollmentsUnsubscribe = onSnapshot(
          query(collection(db, "enrollments"), where("studentId", "==", auth.currentUser!.uid)),
          (snapshot) => {
            setEnrollments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          }
        );
        unsubscribers.push(enrollmentsUnsubscribe);

        // Set up real-time listener for payments for this student
        const paymentsUnsubscribe = onSnapshot(
          query(collection(db, "payments"), where("studentId", "==", auth.currentUser!.uid)),
          (snapshot) => {
            setPayments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          }
        );
        unsubscribers.push(paymentsUnsubscribe);

        // Set up real-time listener for schedule for this student
        const scheduleUnsubscribe = onSnapshot(
          query(collection(db, "schedules"), where("studentId", "==", auth.currentUser!.uid)),
          (snapshot) => {
            const scheduleData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setSchedule(scheduleData);
            
            // Fetch instructors for the schedules
            const instructorIds = Array.from(new Set(scheduleData.map((s: any) => s.instructorId).filter(Boolean)));
            if (instructorIds.length > 0) {
              fetchInstructors(instructorIds);
            }
          }
        );
        unsubscribers.push(scheduleUnsubscribe);

        // Set up real-time listener for schedule preferences for this student
        const schedulePreferencesUnsubscribe = onSnapshot(
          query(collection(db, "schedulePreferences"), where("studentId", "==", auth.currentUser!.uid)),
          (snapshot) => {
            setScheduleRequests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          }
        );
        unsubscribers.push(schedulePreferencesUnsubscribe);

        // Set up real-time listener for student preferences
        const preferencesUnsubscribe = onSnapshot(
          query(collection(db, "studentPreferences"), where("studentId", "==", auth.currentUser!.uid)),
          (snapshot) => {
            const prefData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setPreferences(prefData.length > 0 ? prefData[0] : null);
          }
        );
        unsubscribers.push(preferencesUnsubscribe);

        // Set up real-time listeners for time slots and scheduled slots if branch is available
        if (userData?.branch) {
          const timeSlotsUnsubscribe = onSnapshot(
            query(collection(db, "timeSlots"), where("branch", "==", userData.branch)),
            (snapshot) => {
              const fetchedTimeSlots = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              setTimeSlots(fetchedTimeSlots);
            }
          );
          unsubscribers.push(timeSlotsUnsubscribe);

          const scheduledSlotsUnsubscribe = onSnapshot(collection(db, "schedules"), (snapshot) => {
            setScheduledSlots(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
          });
          unsubscribers.push(scheduledSlotsUnsubscribe);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error setting up real-time listeners:", error);
        setLoading(false);
      }
    };

    setupRealtimeListeners();

    // Cleanup function to unsubscribe from all listeners
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    
    // Debug logging for schedules and enrollments
    if (enrollments.length > 0) {
      console.log("🎓 DEBUG - Student Dashboard Data:", {
        enrollmentsCount: enrollments.length,
        schedulesCount: schedule.length,
        enrollments: enrollments.map(e => ({
          id: e.id,
          courseId: e.courseId,
          status: e.status,
          courseName: courses.find(c => c.id === e.courseId)?.title || 'Unknown'
        })),
        schedules: schedule.map(s => ({
          id: s.id,
          enrollmentId: s.enrollmentId,
          courseId: s.courseId,
          date: s.date,
          status: s.status,
          time: s.time
        })),
        schedulesByEnrollment: enrollments.map(e => ({
          enrollmentId: e.id,
          courseName: courses.find(c => c.id === e.courseId)?.title || 'Unknown',
          schedulesForThisEnrollment: schedule.filter(s => s.enrollmentId === e.id).length
        }))
      });
    }
    const hasPendingEnrollment = enrollments.some(e => e.status === "pending");
    if (
      enrollments.length === 0 &&
      !showOnboarding &&
      !hasPendingEnrollment
    ) {
      setShowOnboarding(true);
    } else if (hasPendingEnrollment) {
      setShowOnboarding(false);
    }
  }, [enrollments, loading]);

  const handleEnroll = async (courseId: string) => {
    if (!auth.currentUser) {
      setMessage({ open: true, text: "You must be logged in to enroll.", type: "error" });
      return;
    }
    if (enrollments.some(e => e.courseId === courseId)) {
      setMessage({ open: true, text: "You are already enrolled in this course.", type: "info" });
      return;
    }
    try {
      // Always fetch the latest user profile to get the branch (by document ID)
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      const userData = userDoc.data();
      const branch = userData?.branch || "";
      if (!branch) {
        setMessage({ open: true, text: "Your branch is not set. Please contact support.", type: "error" });
        return;
      }
      const courseFee = enrollDialog.course?.price || 0;
      const enrollmentDoc = await addDoc(collection(db, "enrollments"), {
        studentId: auth.currentUser.uid,
        courseId,
        branch,
        courseFee,
        status: "pending", // Changed from "payment_pending" to "pending"
        enrolledAt: Timestamp.now(),
        registrationType: "online", // Add registration type for online students
      });
      
      // Create notifications for staff members of the same branch
      const staffSnap = await getDocs(query(
        collection(db, "users"), 
        where("role", "==", "staff"),
        where("branch", "==", branch)
      ));
      
      const studentName = `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim();
      const courseName = enrollDialog.course?.name || enrollDialog.course?.title || "Unknown Course";
      
      for (const staffDoc of staffSnap.docs) {
        await addDoc(collection(db, "notifications"), {
          userId: staffDoc.id,
          type: "new_enrollment",
          title: "New Enrollment",
          message: `${studentName} has enrolled in ${courseName}. Waiting for schedule preferences and payment upload.`,
          read: false,
          createdAt: new Date(),
          relatedId: enrollmentDoc.id
        });
      }
      
      // After enrollment, redirect to preferences to set schedule first
      const newEnrollment = {
        id: enrollmentDoc.id,
        studentId: auth.currentUser.uid,
        courseId,
        branch,
        courseFee,
        status: "pending",
        enrolledAt: Timestamp.now(),
      };
      
      setEnrollDialog({ open: false, course: null });
      
      // Show message about setting preferences first
      setMessage({ 
        open: true, 
        text: "Enrollment successful! Please set your schedule preferences before uploading payment.", 
        type: "success" 
      });
      
      // Switch to preferences tab
      setTimeout(() => {
        setActiveTab("preferences");
      }, 2000);
      
      // Refresh enrollments
      const enrollSnap = await getDocs(query(collection(db, "enrollments"), where("studentId", "==", auth.currentUser.uid)));
      setEnrollments(enrollSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err: any) {
      setMessage({ open: true, text: "Failed to enroll: " + err.message, type: "error" });
    }
  };

  // Add effect to log userBranch and branches when enroll dialog opens
  useEffect(() => {
    if (enrollDialog.open) {
      console.log("Enroll dialog opened. userBranch:", userBranch);
      console.log("Available branch IDs:", branches.map(b => b.id));
    }
  }, [enrollDialog.open, userBranch, branches]);

  // Payment upload handler
  const handlePaymentUpload = async (file: File, enrollmentId: string) => {
    if (!auth.currentUser) {
      setMessage({ open: true, text: "You must be logged in to upload payment.", type: "error" });
      return;
    }
    try {
      // Upload file to Firebase Storage
      const storageRef = ref(storage, `payments/${auth.currentUser.uid}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      // Save payment record in Firestore
      await addDoc(collection(db, "payments"), {
        studentId: auth.currentUser.uid,
        enrollmentId,
        imageUrl: url,
        status: "pending_verification",
        uploadedAt: Timestamp.now(),
        branch: userBranch,
      });
      
      // Update enrollment status and add payment receipt URL
      await updateDoc(doc(db, "enrollments", enrollmentId), {
        status: "pending",
        paymentUploaded: true,
        paymentReceiptUrl: url, // Add the receipt URL to enrollment
        paymentUploadedAt: new Date(),
      });
      
      setMessage({ open: true, text: "Payment uploaded successfully! Waiting for staff verification.", type: "success" });
      setPaymentDialog({ open: false, enrollment: null });
      
      // Refresh enrollments
      const enrollSnap = await getDocs(query(collection(db, "enrollments"), where("studentId", "==", auth.currentUser.uid)));
      setEnrollments(enrollSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err: any) {
      setMessage({ open: true, text: "Failed to upload payment: " + err.message, type: "error" });
    }
  };

  // Schedule request handler
  const handleScheduleRequest = async (enrollmentId: string, timeSlotId: string, selectedDate: string, notes: string) => {
    if (!auth.currentUser) {
      setMessage({ open: true, text: "You must be logged in to submit schedule preferences.", type: "error" });
      return;
    }
    try {
      // Get enrollment and course details
      const enrollment = enrollments.find(e => e.id === enrollmentId);
      const course = courses.find(c => c.id === enrollment?.courseId);
      const timeSlot = timeSlots.find(t => t.id === timeSlotId);
      
      // Get user data
      const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
      const userData = userDoc.data();
      
      // Check slot availability
      const existingBookings = scheduledSlots.filter(
        slot => slot.timeSlotId === timeSlotId && slot.date === selectedDate && slot.status === "scheduled"
      );
      
      if (existingBookings.length >= timeSlot?.maxStudents) {
        setMessage({ open: true, text: "This time slot is fully booked. Please select another slot.", type: "error" });
        return;
      }
      
      await addDoc(collection(db, "schedulePreferences"), {
        studentId: auth.currentUser.uid,
        enrollmentId,
        courseId: enrollment?.courseId,
        preferredDayOfWeek: format(new Date(selectedDate), 'EEEE'), // Store day of week
        preferredTimeSlotId: timeSlotId,
        notes,
        type: "preference",
        status: "submitted",
        submittedAt: Timestamp.now(),
        branch: userBranch,
      });
      
      // Create notifications for staff members of the same branch
      const staffSnap = await getDocs(query(
        collection(db, "users"), 
        where("role", "==", "staff"),
        where("branch", "==", userBranch)
      ));
      
      const studentName = `${userData?.firstName || ""} ${userData?.lastName || ""}`.trim();
      const courseName = course?.name || course?.title || "Unknown Course";
      const timeDisplay = `${formatTime12Hour(timeSlot?.startTime)} - ${formatTime12Hour(timeSlot?.endTime)}`;
      
      for (const staffDoc of staffSnap.docs) {
        await addDoc(collection(db, "notifications"), {
          userId: staffDoc.id,
          type: "schedule_preference",
          title: "New Schedule Preference",
          message: `${studentName} has submitted schedule preferences for ${courseName}. Preferred Day: ${format(new Date(selectedDate), 'EEEE')}, Preferred Time: ${timeDisplay}`,
          read: false,
          createdAt: new Date(),
          relatedId: enrollmentId
        });
      }
      
      setMessage({ open: true, text: "Schedule preferences submitted! Staff will review and create your schedule.", type: "success" });
      setScheduleRequestDialog({ open: false, enrollment: null });
      
      // Redirect to schedule tab after successful submission
      setTimeout(() => {
        setActiveTab("schedule");
      }, 2000);
    } catch (err: any) {
      setMessage({ open: true, text: "Failed to submit schedule request: " + err.message, type: "error" });
    }
  };

  // Save student preferences
  const handleSavePreferences = async (prefData: any) => {
    if (!auth.currentUser) {
      setMessage({ open: true, text: "You must be logged in to save preferences.", type: "error" });
      return;
    }
    try {
      if (preferences?.id) {
        // Update existing preferences
        await updateDoc(doc(db, "studentPreferences", preferences.id), {
          ...prefData,
          updatedAt: Timestamp.now(),
        });
      } else {
        // Create new preferences
        await addDoc(collection(db, "studentPreferences"), {
          studentId: auth.currentUser.uid,
          ...prefData,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
      
      // Check if there are pending enrollments waiting for payment
      const pendingEnrollmentsWithoutPayment = enrollments.filter(e => 
        e.status === "pending" && !e.paymentReceiptUrl
      );
      
      if (pendingEnrollmentsWithoutPayment.length > 0) {
        setMessage({ 
          open: true, 
          text: "Preferences saved! You can now upload payment for your enrollment(s). Redirecting to courses...", 
          type: "success" 
        });
        // Redirect to courses tab after a delay
        setTimeout(() => {
          setActiveTab("courses");
        }, 2000);
      } else {
        setMessage({ open: true, text: "Preferences saved successfully!", type: "success" });
      }
      
      setEditingPreferences(false);
    } catch (err: any) {
      setMessage({ open: true, text: "Failed to save preferences: " + err.message, type: "error" });
    }
  };

  // Inline PaymentUploadForm component
  const PaymentUploadInlineForm = ({ enrollmentId, onUpload }: { enrollmentId: string; onUpload: (file: File, enrollmentId: string) => void }) => {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!file) {
        setMessage({ open: true, text: "Please select a file to upload.", type: "error" });
        return;
      }
      setUploading(true);
      await onUpload(file, enrollmentId);
      setUploading(false);
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Payment Receipt</label>
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            required
          />
        </div>
        <Button type="submit" disabled={uploading || !file} className="w-full">
          {uploading ? "Uploading..." : "Upload Payment"}
        </Button>
      </form>
    );
  };

  // Inline ScheduleRequestForm component
  const ScheduleRequestInlineForm = ({ enrollment, courses, onSubmit }: { enrollment: any; courses: any[]; onSubmit: (enrollmentId: string, timeSlotId: string, date: string, notes: string) => void }) => {
    const [selectedDayOfWeek, setSelectedDayOfWeek] = useState("");
    const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const daysOfWeek = [
      { value: "monday", label: "Monday" },
      { value: "tuesday", label: "Tuesday" },
      { value: "wednesday", label: "Wednesday" },
      { value: "thursday", label: "Thursday" },
      { value: "friday", label: "Friday" },
      { value: "saturday", label: "Saturday" },
      { value: "sunday", label: "Sunday" },
    ];

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!selectedDayOfWeek || !selectedTimeSlot || !selectedDate) {
        setMessage({ open: true, text: "Please select a day, time slot, and specific date.", type: "error" });
        return;
      }
      setSubmitting(true);
      await onSubmit(enrollment.id, selectedTimeSlot, selectedDate, notes);
      setSubmitting(false);
    };

    // Filter time slots for selected day
    const availableTimeSlots = timeSlots
      .filter(slot => slot.dayOfWeek === selectedDayOfWeek)
      .sort((a, b) => {
        const timeA = a.startTime.padStart(5, '0');
        const timeB = b.startTime.padStart(5, '0');
        return timeA.localeCompare(timeB);
      });

    // Check slot availability for a specific date
    const getSlotAvailability = (slotId: string, date: string) => {
      // First check if the slot is available at all (not blocked by completed sessions)
      if (!isTimeSlotAvailable(slotId, date)) {
        const timeSlot = timeSlots.find(slot => slot.id === slotId);
        return { booked: timeSlot?.maxStudents || 1, max: timeSlot?.maxStudents || 1, available: 0 };
      }
      
      // Only count scheduled sessions for occupancy (not completed ones)
      const booked = scheduledSlots.filter(
        scheduled => 
          scheduled.timeSlotId === slotId && 
          scheduled.date === date && 
          scheduled.status === "scheduled"
      ).length;
      const timeSlot = timeSlots.find(slot => slot.id === slotId);
      return { booked, max: timeSlot?.maxStudents || 1, available: (timeSlot?.maxStudents || 1) - booked };
    };

    // Get next available dates for the selected day of week
    const getNextDatesForDay = (dayOfWeek: string) => {
      const dates = [];
      const today = new Date();
      const dayIndex = daysOfWeek.findIndex(d => d.value === dayOfWeek);
      
      for (let i = 0; i < 30; i++) { // Look ahead 30 days
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        if (date.getDay() === dayIndex) {
          dates.push(date.toISOString().split('T')[0]);
        }
        if (dates.length >= 4) break; // Show next 4 occurrences
      }
      return dates;
    };

    const availableDates = selectedDayOfWeek ? getNextDatesForDay(selectedDayOfWeek) : [];

    // Helper function to check if a time slot is available
    // Check if the number of schedules for this slot+date is less than maxStudents
    const isTimeSlotAvailable = (timeSlotId: string, date: string) => {
      const timeSlot = timeSlots.find(slot => slot.id === timeSlotId);
      if (!timeSlot) return false;
      
      const schedulesForSlot = scheduledSlots.filter(schedule => 
        schedule.timeSlotId === timeSlotId && 
        schedule.date === date
      );
      
      return schedulesForSlot.length < timeSlot.maxStudents;
    };

    const availableDatesWithSlots = selectedDate ? availableDates.filter(date => {
      const dateSlots = timeSlots.filter(slot => slot.dayOfWeek === selectedDayOfWeek);
      return dateSlots.some(slot => isTimeSlotAvailable(slot.id, date));
    }) : [];

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="mb-2">
          <strong>Course:</strong> {courses.find(c => c.id === enrollment?.courseId)?.title}
        </div>
        
        <div>
          <label className="block text-sm font-medium mb-2">Select Day of Week</label>
          <select
            value={selectedDayOfWeek}
            onChange={(e) => {
              setSelectedDayOfWeek(e.target.value);
              setSelectedTimeSlot("");
              setSelectedDate("");
            }}
            className="w-full border rounded p-2"
            required
          >
            <option value="">Choose a day</option>
            {daysOfWeek.map((day) => (
              <option key={day.value} value={day.value}>
                {day.label}
              </option>
            ))}
          </select>
        </div>

        {selectedDayOfWeek && (
          <div>
            <label className="block text-sm font-medium mb-2">Available Time Slots</label>
            {availableTimeSlots.length === 0 ? (
              <div className="text-sm text-gray-500 p-3 border rounded">
                No time slots available for {daysOfWeek.find(d => d.value === selectedDayOfWeek)?.label}.
              </div>
            ) : (
              <div className="space-y-2">
                {availableTimeSlots.map(slot => {
                  const isSelected = selectedTimeSlot === slot.id;
                  
                  return (
                    <div 
                      key={slot.id}
                      className={`border rounded p-3 cursor-pointer transition-colors ${
                        isSelected 
                          ? "border-blue-500 bg-blue-50" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => {
                        setSelectedTimeSlot(slot.id);
                        setSelectedDate(""); // Reset date when time slot changes
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">
                            {formatTime12Hour(slot.startTime)} - {formatTime12Hour(slot.endTime)}
                          </div>
                          <div className="text-sm text-gray-600">
                            Max {slot.maxStudents} students
                          </div>
                        </div>
                        <div>
                          {isSelected && (
                            <div className="w-4 h-4 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {selectedTimeSlot && (
          <div>
            <label className="block text-sm font-medium mb-2">Select Specific Date</label>
            <select
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full border rounded p-2"
              required
            >
              <option value="">Choose a date</option>
              {availableDatesWithSlots.map((date) => {
                const availability = getSlotAvailability(selectedTimeSlot, date);
                const isAvailable = availability.available > 0;
                
                return (
                  <option key={date} value={date} disabled={!isAvailable}>
                    {new Date(date).toLocaleDateString()} 
                    {isAvailable 
                      ? ` (${availability.available}/${availability.max} slots available)`
                      : ' (Fully booked)'
                    }
                  </option>
                );
              })}
            </select>
          </div>
        )}
        
        <div>
          <label className="block text-sm font-medium mb-2">Additional Notes (Optional)</label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any specific requests or notes for your schedule..."
            rows={3}
          />
        </div>
        <Button 
          type="submit" 
          disabled={submitting || !selectedDate || !selectedTimeSlot} 
          className="w-full"
        >
          {submitting ? "Submitting..." : "Submit Preferences"}
        </Button>
      </form>
    );
  };

  // Inline License Payment Upload Form component
  const LicensePaymentUploadInlineForm = ({ requestId, onUpload }: { requestId: string; onUpload: (file: File, requestId: string) => void }) => {
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!file) {
        setMessage({ open: true, text: "Please select a file to upload.", type: "error" });
        return;
      }
      setUploading(true);
      await onUpload(file, requestId);
      setUploading(false);
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Payment Receipt</label>
          <Input
            type="file"
            accept="image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            required
          />
        </div>
        <Button type="submit" disabled={uploading || !file} className="w-full">
          {uploading ? "Uploading..." : "Upload Payment"}
        </Button>
      </form>
    );
  };

  return (
    <div className="flex h-full">
      <StudentSidebar activeTab={activeTab} setActiveTab={setActiveTab} />
      <div className="flex-1 p-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <svg className="animate-spin h-8 w-8 text-blue-600 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
            <span className="text-lg text-blue-600">Loading...</span>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-bold mb-4">Student Dashboard</h1>
            <UIDialog open={showOnboarding} onOpenChange={setShowOnboarding}>
              <UIDialogContent>
                <UIDialogHeader>
                  <UIDialogTitle>Welcome! Get Started</UIDialogTitle>
                </UIDialogHeader>
                <div className="mb-4">It looks like you haven't enrolled in any course or requested a license code yet. You can do both now, or skip for later.</div>
                <div className="space-y-4">
                  <button
                    className="w-full bg-blue-600 text-white rounded px-4 py-2"
                    onClick={() => {
                      setShowOnboarding(false);
                      setActiveTab("courses");
                    }}
                  >
                    Enroll in a Course
                  </button>
                  <button
                    className="w-full bg-gray-300 text-gray-800 rounded px-4 py-2"
                    onClick={() => setShowOnboarding(false)}
                  >
                    Skip for now
                  </button>
                </div>
              </UIDialogContent>
            </UIDialog>
            {activeTab === "courses" && (
              <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {courses.filter(course => {
                  // Filter courses to show only those available for the student's branch
                  // If course has no branches array, show it (for backward compatibility)
                  // If course has branches array, show it only if student's branch is included
                  return !course.branches || course.branches.length === 0 || course.branches.includes(userBranch);
                }).sort((a, b) => {
                  // Sort by displayOrder (branch-specific first, then general)
                  const aOrder = a[`displayOrder_${userBranch}`] ?? a.displayOrder ?? 999999;
                  const bOrder = b[`displayOrder_${userBranch}`] ?? b.displayOrder ?? 999999;
                  return aOrder - bOrder;
                }).map((c) => {
                  const enrolled = enrollments.find(e => e.courseId === c.id);
                  return (
                    <Card key={c.id} className="flex flex-col min-h-[400px]">
                      <CardHeader className="pb-4">
                        <CardTitle className="text-lg">{c.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col">
                        <div className="mb-3 text-muted-foreground text-sm leading-relaxed">{c.description}</div>
                        <div className="mb-4 text-sm font-medium">Price: ₱{c.price}</div>
                        <div className="mt-auto">
                        {enrolled ? (
                          <div className="space-y-2">
                            {/* Course progress display */}
                            {enrolled.completedHours !== undefined && enrolled.totalHours !== undefined && (
                              <div className="mb-2">
                                <div className="text-sm text-gray-600 mb-1">
                                  Progress: {enrolled.completedHours}/{enrolled.totalHours} hours
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full" 
                                    style={{ width: `${Math.min((enrolled.completedHours / enrolled.totalHours) * 100, 100)}%` }}
                                  />
                                </div>
                              </div>
                            )}
                            
                            <div className={`p-2 rounded text-sm ${
                              enrolled.status === "pending" ? (enrolled.paymentReceiptUrl ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800") :
                              enrolled.status === "pending_approval" ? "bg-yellow-100 text-yellow-800" :
                              enrolled.status === "approved" ? "bg-green-100 text-green-800" :
                              enrolled.status === "scheduled" ? "bg-blue-100 text-blue-800" :
                              enrolled.status === "completed" ? "bg-purple-100 text-purple-800" :
                              "bg-gray-100 text-gray-800"
                            }`}>
                              Status: {
                                enrolled.status === "pending" ? (() => {
                                  if (enrolled.paymentReceiptUrl) {
                                    return "Pending Staff Review";
                                  }
                                  // Check if preferences are set
                                  const hasPreferences = preferences && 
                                    preferences.preferredDays && 
                                    preferences.preferredDays.length > 0 &&
                                    preferences.preferredTimeSessions &&
                                    preferences.preferredTimeSessions.length > 0;
                                  return hasPreferences ? "Payment Required" : "Set Schedule Preferences First";
                                })() :
                                enrolled.status === "pending_approval" ? "Pending Approval" :
                                enrolled.status === "approved" ? (() => {
                                  // Check if there are any schedules for this enrollment
                                  const enrollmentSchedules = schedule.filter(s => s.enrollmentId === enrolled.id);
                                  if (enrollmentSchedules.length > 0) {
                                    // Check for upcoming scheduled sessions
                                    const upcomingSchedule = enrollmentSchedules.find(s => 
                                      s.status === "scheduled" && 
                                      new Date(s.date) >= new Date()
                                    );
                                    if (upcomingSchedule) {
                                      return "Scheduled";
                                    }
                                    // Has schedules but no upcoming ones
                                    return "In Progress";
                                  }
                                  return "Approved - Waiting for Staff to Schedule";
                                })() :
                                enrolled.status === "scheduled" ? "Scheduled" :
                                enrolled.status === "completed" ? "Course Completed" :
                                enrolled.status
                              }
                            </div>
                            {enrolled.status === "pending" && (() => {
                              // Check if student has set preferences first
                              const hasPreferences = preferences && 
                                preferences.preferredDays && 
                                preferences.preferredDays.length > 0 &&
                                preferences.preferredTimeSessions &&
                                preferences.preferredTimeSessions.length > 0;
                              
                              if (!hasPreferences) {
                                return (
                                  <Button
                                    size="sm"
                                    className="w-full"
                                    variant="outline"
                                    onClick={() => setActiveTab("preferences")}
                                  >
                                    Set Preferences First
                                  </Button>
                                );
                              }
                              
                              return (
                                <div className="space-y-2">
                                  <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                                    <strong>Note:</strong> Our staff will call you to discuss payment details before you upload your payment receipt.
                                  </div>
                                  <Button
                                    size="sm"
                                    className="w-full"
                                    onClick={() => setPaymentDialog({ open: true, enrollment: enrolled })}
                                    disabled={enrolled.paymentReceiptUrl}
                                  >
                                    {enrolled.paymentReceiptUrl ? "Payment Uploaded" : "Upload Payment"}
                                  </Button>
                                </div>
                              );
                            })()}
                            {enrolled.status === "approved" && (() => {
                              // Check if there's already a pending schedule request for this enrollment
                              const pendingRequest = scheduleRequests.find(
                                req => req.enrollmentId === enrolled.id && req.status === "pending"
                              );
                              
                              if (pendingRequest) {
                                return (
                                  <Button
                                    size="sm"
                                    className="w-full"
                                    variant="outline"
                                    disabled
                                  >
                                    Schedule Request Pending
                                  </Button>
                                );
                              }
                              
                              // Check if there are ANY schedules for this enrollment (past, present, or future)
                              const enrollmentSchedules = schedule.filter(s => s.enrollmentId === enrolled.id);
                              
                              if (enrollmentSchedules.length > 0) {
                                // Check if there's an upcoming scheduled session
                                const upcomingSchedule = enrollmentSchedules.find(s => 
                                  s.status === "scheduled" && 
                                  new Date(s.date) >= new Date()
                                );
                                
                                return (
                                  <Button
                                    size="sm"
                                    className="w-full"
                                    variant="outline"
                                    onClick={() => setActiveTab("schedule")}
                                  >
                                    {upcomingSchedule ? "View Current Schedule" : "View Schedule"}
                                  </Button>
                                );
                              }
                              
                              // No schedules exist for this enrollment yet - waiting for staff to schedule
                              return (
                                <Button
                                  size="sm"
                                  className="w-full"
                                  variant="outline"
                                  disabled
                                >
                                  Waiting for Staff to Schedule
                                </Button>
                              );
                            })()}
                            {enrolled.status === "completed" && (
                              <div className="mt-2">
                                {enrolled.certificateUrl ? (
                                  <Button
                                    size="sm"
                                    className="w-full"
                                    onClick={() => window.open(enrolled.certificateUrl, '_blank')}
                                  >
                                    View Certificate
                                  </Button>
                                ) : (
                                  <div className="p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                                    🎓 Course completed! Waiting for staff to upload your certificate.
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="mt-auto">
                            <button
                              className="px-4 py-2 bg-blue-600 text-white rounded-md w-full hover:bg-blue-700 transition-colors"
                              onClick={() => setEnrollDialog({ open: true, course: c })}
                            >
                              Enroll
                            </button>
                          </div>
                        )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                {/* Enroll Dialog */}
                <UIDialog open={enrollDialog.open} onOpenChange={open => setEnrollDialog({ open, course: enrollDialog.course })}>
                  <UIDialogContent>
                    <UIDialogHeader>
                      <UIDialogTitle>Enroll in {enrollDialog.course?.title}</UIDialogTitle>
                    </UIDialogHeader>
                    <div className="mb-2">Course Price: ₱{enrollDialog.course?.price}</div>
                    <div className="mb-2 font-semibold">
                      Branch: {(() => {
                        if (!userBranch) return <span className="text-muted-foreground">Not set</span>;
                        if (!branches.length) return <span className="text-muted-foreground">Loading...</span>;
                        const branchObj = branches.find(b => b.id === userBranch);
                        if (branchObj) return branchObj.name;
                        return <span className="text-red-600">Unknown branch (ID: {userBranch})</span>;
                      })()}
                    </div>
                    <div className="mb-2 font-semibold">
                      Course Fee: ₱{enrollDialog.course?.price?.toLocaleString()}
                    </div>
                    <button
                      className="mt-2 px-3 py-1 bg-blue-600 text-white rounded w-full"
                      onClick={() => handleEnroll(enrollDialog.course.id)}
                      disabled={!userBranch || !branches.length}
                    >
                      Confirm Enrollment
                    </button>
                  </UIDialogContent>
                </UIDialog>
                
                {/* Payment Upload Dialog */}
                <UIDialog open={paymentDialog.open} onOpenChange={open => setPaymentDialog({ open, enrollment: paymentDialog.enrollment })}>
                  <UIDialogContent>
                    <UIDialogHeader>
                      <UIDialogTitle>Upload Payment Receipt</UIDialogTitle>
                    </UIDialogHeader>
                    <div className="space-y-4">
                      <div className="mb-2">
                        <strong>Course:</strong> {courses.find(c => c.id === paymentDialog.enrollment?.courseId)?.title}
                      </div>
                      <div className="mb-2">
                        <strong>Amount:</strong> ₱{paymentDialog.enrollment?.courseFee}
                      </div>
                      <div className="mb-4 p-3 bg-blue-50 rounded">
                        <p className="text-sm text-blue-800">
                          Please pay via GCash and upload your payment receipt. Our staff will verify your payment and approve your enrollment.
                        </p>
                      </div>
                      <PaymentUploadInlineForm 
                        enrollmentId={paymentDialog.enrollment?.id || ""} 
                        onUpload={handlePaymentUpload}
                      />
                    </div>
                  </UIDialogContent>
                </UIDialog>

                {/* Schedule Request Dialog */}
                <UIDialog open={scheduleRequestDialog.open} onOpenChange={open => setScheduleRequestDialog({ open, enrollment: scheduleRequestDialog.enrollment })}>
                  <UIDialogContent>
                    <UIDialogHeader>
                      <UIDialogTitle>Submit Schedule Preferences</UIDialogTitle>
                    </UIDialogHeader>
                    <ScheduleRequestInlineForm 
                      enrollment={scheduleRequestDialog.enrollment}
                      courses={courses}
                      onSubmit={handleScheduleRequest}
                    />
                  </UIDialogContent>
                </UIDialog>
              </div>
            )}
            {activeTab === "schedule" && (
              <div>
                {/* Submitted Schedule Preferences */}
                {scheduleRequests.filter(req => req.status === "submitted").length > 0 && (
                  <Card className="mb-4">
                    <CardHeader><CardTitle>Submitted Schedule Preferences</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid gap-4">
                        {scheduleRequests.filter(req => req.status === "submitted").map((req) => {
                          const enrollment = enrollments.find(e => e.id === req.enrollmentId);
                          const course = courses.find(c => c.id === enrollment?.courseId);
                          return (
                            <div key={req.id} className="border rounded p-3 flex flex-col space-y-2 bg-blue-50">
                              <span className="font-medium">{course?.title || "Unknown Course"}</span>
                              <span>Preferred Day: {req.preferredDayOfWeek}</span>
                              <span>Preferred Time: {(() => {
                                const timeSlot = timeSlots.find(ts => ts.id === req.preferredTimeSlotId);
                                return timeSlot ? `${formatTime12Hour(timeSlot.startTime)} - ${formatTime12Hour(timeSlot.endTime)}` : 'Unknown';
                              })()}</span>
                              <span>Status: <span className="inline-block px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">Submitted - Awaiting Staff Scheduling</span></span>
                              {req.notes && <span className="text-sm text-gray-600">Notes: {req.notes}</span>}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                <Card>
                  <CardHeader><CardTitle>Upcoming Sessions</CardTitle></CardHeader>
                  <CardContent>
                    {schedule.filter(s => s.status !== "rescheduled").length === 0 ? (
                      <div>No sessions scheduled.</div>
                    ) : (
                      <div className="grid gap-4">
                        {schedule.filter(s => s.status !== "rescheduled").map((s) => {
                          const instructor = instructors.find(i => i.id === s.instructorId);
                          const instructorName = instructor ? `${instructor.firstName || ""} ${instructor.lastName || ""}`.trim() : "Instructor not assigned";
                          
                          return (
                            <div key={s.id} className="border rounded p-3 flex flex-col space-y-2">
                              <span className="font-medium">{courses.find(c => c.id === s.courseId)?.title || s.courseId}</span>
                              <span>Date: {s.date}</span>
                              <span>Time: {s.time}</span>
                              <span>Instructor: {instructorName}</span>
                              <span className={`inline-block px-2 py-1 rounded text-xs w-fit ${
                                s.status === "scheduled" ? "bg-blue-100 text-blue-800" :
                                s.status === "completed" ? "bg-green-100 text-green-800" :
                                s.status === "cancelled" ? "bg-red-100 text-red-800" :
                                s.status === "rescheduled" ? "bg-yellow-100 text-yellow-800" :
                                "bg-gray-100 text-gray-800"
                              }`}>
                                {s.status || "Scheduled"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
            {activeTab === "payments" && (
              <div className="mb-8">
                <Card>
                  <CardHeader><CardTitle>Invoices</CardTitle></CardHeader>
                  <CardContent>
                    <table className="w-full border text-sm">
                      <thead>
                        <tr className="bg-muted">
                          <th className="p-2 text-left">Course</th>
                          <th className="p-2 text-left">Status</th>
                          <th className="p-2 text-left">Date</th>
                          <th className="p-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((p) => (
                          <tr key={p.id} className="border-t">
                            <td className="p-2">{courses.find(c => c.id === enrollments.find(e => e.id === p.enrollmentId)?.courseId)?.title || p.enrollmentId}</td>
                            <td className="p-2">{p.status}</td>
                            <td className="p-2">{new Date(p.uploadedAt?.seconds * 1000).toLocaleDateString()}</td>
                            <td className="p-2">
                              <button className="px-2 py-1 bg-blue-500 text-white rounded mr-2">Upload Payment</button>
                              <button className="px-2 py-1 bg-green-500 text-white rounded mr-2">Preview</button>
                              <button className="px-2 py-1 bg-yellow-500 text-white rounded">Edit</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </div>
            )}
          </>        )}
        {activeTab === "preferences" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                Schedule Preferences
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingPreferences(!editingPreferences)}
                >
                  {editingPreferences ? "Cancel" : "Edit"}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {(() => {
                // Check if there are pending enrollments without payment that need preferences
                const pendingEnrollmentsWithoutPayment = enrollments.filter(e => 
                  e.status === "pending" && !e.paymentReceiptUrl
                );
                
                const hasPreferences = preferences && 
                  preferences.preferredDays && 
                  preferences.preferredDays.length > 0 &&
                  preferences.preferredTimeSessions &&
                  preferences.preferredTimeSessions.length > 0;
                  
                if (pendingEnrollmentsWithoutPayment.length > 0 && !hasPreferences) {
                  return (
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg mb-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-orange-500 rounded-full flex-shrink-0"></div>
                        <div>
                          <p className="font-medium text-orange-800">Action Required</p>
                          <p className="text-sm text-orange-700">
                            You have {pendingEnrollmentsWithoutPayment.length} pending enrollment(s). 
                            Please set your schedule preferences before uploading payment.
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              <div className="text-sm text-muted-foreground mb-4">
                Set your preferred days and session times for driving lessons. Choose morning (8AM-12PM) or afternoon (1PM-6PM) sessions. Staff will consider these preferences when scheduling your sessions.
              </div>
              
              {editingPreferences ? (
                <div className="space-y-4" data-preferences-form>
                  <div>
                    <h4 className="font-medium mb-2">Preferred Days</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map(day => {
                        const currentPrefs = preferences?.preferredDays || [];
                        return (
                          <label key={day} className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              className="rounded" 
                              defaultChecked={currentPrefs.includes(day)}
                              onChange={(e) => {
                                // This will be handled by the form submission
                              }}
                            />
                            <span className="capitalize">{day}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Preferred Time Sessions</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {['morning', 'afternoon'].map(session => {
                        const currentTimePrefs = preferences?.preferredTimeSessions || [];
                        return (
                          <label key={session} className="flex items-center space-x-2">
                            <input 
                              type="checkbox" 
                              className="rounded" 
                              defaultChecked={currentTimePrefs.includes(session)}
                              onChange={(e) => {
                                // This will be handled by the form submission
                              }}
                            />
                            <span className="capitalize">
                              {session} Session
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-2">Notes</h4>
                    <Textarea 
                      placeholder="Any specific preferences..." 
                      defaultValue={preferences?.notes || ""}
                      id="preference-notes"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => {
                      // Get form data and save
                      const form = document.querySelector('div[data-preferences-form]') as HTMLDivElement;
                      const dayCheckboxes = form?.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
                      const notesTextarea = document.getElementById('preference-notes') as HTMLTextAreaElement;
                      
                      const selectedDays: string[] = [];
                      const selectedTimeSessions: string[] = [];
                      
                      dayCheckboxes?.forEach((checkbox, index) => {
                        if (checkbox.checked) {
                          if (index < 7) { // First 7 are days
                            const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
                            selectedDays.push(days[index]);
                          } else { // Rest are time sessions (morning, afternoon)
                            const sessionIndex = index - 7;
                            const sessions = ["morning", "afternoon"];
                            if (sessions[sessionIndex]) {
                              selectedTimeSessions.push(sessions[sessionIndex]);
                            }
                          }
                        }
                      });
                      
                      handleSavePreferences({ 
                        preferredDays: selectedDays, 
                        preferredTimeSessions: selectedTimeSessions,
                        notes: notesTextarea?.value || "" 
                      });
                    }}>Save</Button>
                    <Button variant="outline" onClick={() => setEditingPreferences(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {!preferences ? (
                    <div className="text-center py-8">
                      <p className="text-muted-foreground mb-4">No preferences set yet.</p>
                      <p className="text-sm text-muted-foreground">Click "Edit" to set your preferred days and times.</p>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">Preferred Days</h4>
                        <div className="flex flex-wrap gap-1">
                          {preferences.preferredDays?.length > 0 ? 
                            preferences.preferredDays.map((day: string) => (
                              <span key={day} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm capitalize">
                                {day}
                              </span>
                            )) : 
                            <span className="text-sm text-muted-foreground">None set</span>
                          }
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <h4 className="font-medium mb-2">Preferred Time Sessions</h4>
                        <div className="flex flex-wrap gap-1">
                          {preferences.preferredTimeSessions?.length > 0 ? 
                            preferences.preferredTimeSessions.map((session: string) => (
                              <span key={session} className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm capitalize">
                                {session} Session
                              </span>
                            )) : 
                            <span className="text-sm text-muted-foreground">None set</span>
                          }
                        </div>
                      </div>
                      
                      {preferences.notes && (
                        <div>
                          <h4 className="font-medium mb-2">Notes</h4>
                          <p className="text-sm text-muted-foreground bg-gray-50 rounded p-2">{preferences.notes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        {activeTab === "notifications" && <StudentNotifications />}

        <MessageDialog open={message.open} onClose={() => setMessage({ ...message, open: false })} message={message.text} type={message.type} />
      </div>
    </div>
  );
};

export default StudentDashboard;
